import React, { useState, useEffect } from 'react';
import {
  element,
  oneOfType,
  string,
  func,
  object,
  bool,
  array,
  number,
} from 'prop-types';
import styled from 'styled-components';
import Modal from 'react-modal';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import theme from '@airasia-common/libraries/theming/theme';

import loadableVisibility from 'react-loadable-visibility/loadable-components';
import { BodyText } from '../Text/Heading';
import { useWindowSize } from '../../utils';
const SortAndFilter = loadableVisibility(() =>
  import(/* webpackChunkName:"SortAndFilter"*/ './SortAndFilter')
);

const mobileBreakPoint = parseInt(theme.breakpoints[0].replace('px', ''));

const modalStyle = {
  content: {
    top: 0,
    left: 0,
    padding: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
  overlay: {
    zIndex: 100,
  },
};

const MobileHeader = styled(BodyText)`
  margin: 16px 0;
  line-height: 19px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

MobileHeader.defaultProps = {
  color: 'greyDark',
  display: ['flex', 'none'],
};

const SortAndFilterButton = styled.button`
  border: 1px solid
    ${({ theme, active, move }) =>
      theme.colors[
        active
          ? (move && 'secondaryMove') || 'secondary'
          : (move && 'starEmptyColor') || 'greyLight'
      ]};
  box-shadow: none;
  border-radius: 40px;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  padding: 8px 15px;
  color: ${({ theme, active, move }) =>
    theme.colors[
      active ? (move && 'secondaryMove') || 'secondary' : 'darkBlack'
    ]};
  display: flex;
  outline: none;
  background-color: ${({ theme }) => theme.colors.primaryWhite};
`;

const ActiveFilterMark = styled.span`
  width: 6px;
  height: 6px;
  background: ${({ theme, move }) =>
    move ? theme.colors.secondaryMove : theme.colors.secondary};
  display: inline-block;
  border-radius: 6px;
  margin-left: 2px;
`;

const MobileListHeader = ({
  children,
  updateFilter,
  clearFilter,
  filter,
  initialPropertyTypes,
  showPropertyType,
  propertyTypeSelectionHandler,
  hotelClassSelectionHandler,
  isShowChecklist,
  sendAmenitiesId,
  initialAmenities,
  locationOptions,
  isLocationFilterEnabled,
  isShowInputFilter,
  listFilterItems,
  onClear,
  cancelIconColor,
  searchInputHandle,
  onSelectItemFilter,
  filterPlaceHolder,
  onUpdateFilterTrigger,
  selectedLocationId,
  locationSelectionHandler,
  isEmpty,
  data,
  enableHotelNameSearch,
  locale,
  lat,
  lon,
  clientId,
  dest,
  onSelectDropdownOptions,
  onApplyButtonClick,
  hotelNameObj,
  resetHotelName,
  environment,
  showAbleLocationLength,
  sortBySelectionHandler,
  guestRatingSelectionHandler,
  priceFilterHandler,
  showAbleAmenityLength,
  lob,
  isNeighborhoodFilterEnabled,
  neighborhoodOptions,
  neighborhoodSelectionHandler,
  showPaymentAndCancellationPolicyFilter,
  paymentAndCancellationPolicyHandler,
  isSnap,
  isHotel,
  showPromotionFilter,
  promotionFilterSelectionHandler,
  showPromotionFilterCheckBox,
  enableFilterRearrange,
  enableFreeCancellation,
  enableGuestRatingDeselect,
  enablePayNowFilter,
  isMoveEnabled,
  theme,
  enablePayLaterFilter,
  enablePriceBucketsConfig,
  priceRange,
  isTotalGuestPriceEnable,
}) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const { width } = useWindowSize();

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (width > mobileBreakPoint && modalIsOpen) {
      closeModal();
    }
  }, [width, modalIsOpen]);

  return (
    <MobileHeader>
      {children}
      <SortAndFilterButton
        id="sort-filter-button"
        tertiary
        onClick={openModal}
        active={filter.isApplying}
        move={isMoveEnabled}
      >
        {translate('sortAndFilter.button')}
        {filter.isApplying && <ActiveFilterMark move={isMoveEnabled} />}
      </SortAndFilterButton>
      <Modal
        id="sort-filter-popup"
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={modalStyle}
      >
        <SortAndFilter
          theme={theme}
          isMoveEnabled={isMoveEnabled}
          isMobile
          closeModal={closeModal}
          filter={filter}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          hotelClassSelectionHandler={hotelClassSelectionHandler}
          isShowChecklist={isShowChecklist}
          sendAmenitiesId={sendAmenitiesId}
          initialAmenities={initialAmenities}
          locationOptions={locationOptions}
          isLocationFilterEnabled={isLocationFilterEnabled}
          isShowInputFilter={isShowInputFilter}
          listFilterItems={listFilterItems}
          onClear={onClear}
          cancelIconColor={cancelIconColor}
          searchInputHandle={searchInputHandle}
          onSelectItemFilter={onSelectItemFilter}
          filterPlaceHolder={filterPlaceHolder}
          onUpdateFilterTrigger={onUpdateFilterTrigger}
          locationSelectionHandler={locationSelectionHandler}
          selectedLocationId={selectedLocationId}
          isEmpty={isEmpty}
          enableHotelNameSearch={enableHotelNameSearch}
          data={data}
          locale={locale}
          lat={lat}
          lon={lon}
          clientId={clientId}
          dest={dest}
          onSelectDropdownOptions={onSelectDropdownOptions}
          onApplyButtonClick={onApplyButtonClick}
          hotelNameObj={hotelNameObj}
          resetHotelName={resetHotelName}
          environment={environment}
          showAbleLocationLength={showAbleLocationLength}
          sortBySelectionHandler={sortBySelectionHandler}
          guestRatingSelectionHandler={guestRatingSelectionHandler}
          priceFilterHandler={priceFilterHandler}
          showAbleAmenityLength={showAbleAmenityLength}
          lob={lob}
          isNeighborhoodFilterEnabled={isNeighborhoodFilterEnabled}
          neighborhoodOptions={neighborhoodOptions}
          neighborhoodSelectionHandler={neighborhoodSelectionHandler}
          showPaymentAndCancellationPolicyFilter={
            showPaymentAndCancellationPolicyFilter
          }
          paymentAndCancellationPolicyHandler={
            paymentAndCancellationPolicyHandler
          }
          initialPropertyTypes={initialPropertyTypes}
          showPropertyType={showPropertyType}
          propertyTypeSelectionHandler={propertyTypeSelectionHandler}
          isSnap={isSnap}
          isHotel={isHotel}
          showPromotionFilter={showPromotionFilter}
          promotionFilterSelectionHandler={promotionFilterSelectionHandler}
          showPromotionFilterCheckBox={showPromotionFilterCheckBox}
          enableFilterRearrange={enableFilterRearrange}
          enablePayNowFilter={enablePayNowFilter}
          enableFreeCancellation={enableFreeCancellation}
          enableGuestRatingDeselect={enableGuestRatingDeselect}
          enablePayLaterFilter={enablePayLaterFilter}
          enablePriceBucketsConfig={enablePriceBucketsConfig}
          priceRange={priceRange}
          isTotalGuestPriceEnable={isTotalGuestPriceEnable}
        />
      </Modal>
    </MobileHeader>
  );
};

MobileListHeader.propTypes = {
  children: oneOfType([string, element]).isRequired,
  updateFilter: func,
  clearFilter: func,
  filter: object,
  hotelClassSelectionHandler: func,
  isShowChecklist: bool,
  sendAmenitiesId: func,
  initialAmenities: array,
  locationOptions: array,
  isLocationFilterEnabled: bool,
  isShowInputFilter: bool,
  listFilterItems: array,
  onClear: func,
  cancelIconColor: string,
  searchInputHandle: func,
  onSelectItemFilter: func,
  filterPlaceHolder: string,
  onUpdateFilterTrigger: func,
  selectedLocationId: string,
  locationSelectionHandler: func,
  isEmpty: bool,
  enableHotelNameSearch: bool,
  data: object,
  locale: string,
  lat: number,
  lon: number,
  clientId: string,
  dest: string,
  onSelectDropdownOptions: func,
  onApplyButtonClick: func,
  hotelNameObj: object,
  resetHotelName: func,
  environment: string,
  showAbleLocationLength: number,
  showAbleAmenityLength: number,
  sortBySelectionHandler: func,
  guestRatingSelectionHandler: func,
  priceFilterHandler: func,
  lob: string,
  isNeighborhoodFilterEnabled: bool,
  neighborhoodSelectionHandler: func,
  neighborhoodOptions: array,
  showPaymentAndCancellationPolicyFilter: bool,
  paymentAndCancellationPolicyHandler: func,
  initialPropertyTypes: array,
  propertyTypeSelectionHandler: func,
  isSnap: bool,
  isHotel: bool,
  showPromotionFilter: bool,
  promotionFilterSelectionHandler: func,
  showPromotionFilterCheckBox: bool,
  enableFilterRearrange: bool,
  enablePayNowFilter: bool,
  showPropertyType: bool,
  enableFreeCancellation: bool,
  enableGuestRatingDeselect: bool,
  isMoveEnabled: bool,
  theme: string,
  enablePayLaterFilter: bool,
  enablePriceBucketsConfig: bool,
  priceRange: [],
  isTotalGuestPriceEnable: bool,
};

MobileListHeader.defaultProps = {
  hotelClassSelectionHandler: () => {},
  locationOptions: [],
  isLocationFilterEnabled: false,
  isShowInputFilter: false,
  listFilterItems: [],
  selectedLocationId: '',
  locationSelectionHandler: () => {},
  isEmpty: false,
  enableHotelNameSearch: false,
  environment: 'STAGING',
  showAbleLocationLength: 3,
  showAbleAmenityLength: 6,
  sortBySelectionHandler: () => {},
  guestRatingSelectionHandler: () => {},
  priceFilterHandler: () => {},
  lob: 'SNAP',
  isNeighborhoodFilterEnabled: false,
  neighborhoodSelectionHandler: () => {},
  neighborhoodOptions: [],
  showPaymentAndCancellationPolicyFilter: false,
  paymentAndCancellationPolicyHandler: () => {},
  propertyTypeSelectionHandler: () => {},
  showPropertyType: false,
  enablePayNowFilter: false,
  isSnap: false,
  isHotel: false,
  showPromotionFilter: false,
  promotionFilterSelectionHandler: () => {},
  showPromotionFilterCheckBox: true,
  enableFreeCancellation: false,
  enableGuestRatingDeselect: false,
  isMoveEnabled: false,
  enablePayLaterFilter: true,
  enablePriceBucketsConfig: false,
  priceRange: [],
  isTotalGuestPriceEnable: false,
};

export default MobileListHeader;
